import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Download, Filter, Search, Eye, CheckCircle, XCircle, Clock, User } from 'lucide-react';
import api from '../../utils/api';
import toast from 'react-hot-toast';
import { format } from 'date-fns';
import { navigateBack } from '../../utils/navigation';

const AssessmentSubmissions = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [submissions, setSubmissions] = useState([]);
  const [assessment, setAssessment] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    status: '',
    search: ''
  });

  useEffect(() => {
    fetchAssessment();
    fetchSubmissions();
  }, [id]);

  const fetchAssessment = async () => {
    try {
      const { data } = await api.get(`/assessments/${id}`);
      if (data.success) {
        setAssessment(data.data);
      }
    } catch (error) {
      toast.error('Failed to fetch assessment details');
    }
  };

  const fetchSubmissions = async () => {
    try {
      setLoading(true);
      const { data } = await api.get(`/assessments/${id}/submissions`, {
        params: filters
      });
      if (data.success) {
        setSubmissions(data.data);
      }
    } catch (error) {
      toast.error('Failed to fetch submissions');
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      submitted: { label: 'Submitted', color: 'bg-blue-100 text-blue-700 border-blue-200' },
      graded: { label: 'Graded', color: 'bg-green-100 text-green-700 border-green-200' },
      in_progress: { label: 'In Progress', color: 'bg-yellow-100 text-yellow-700 border-yellow-200' },
      not_started: { label: 'Not Started', color: 'bg-gray-100 text-gray-700 border-gray-200' },
    };
    const config = statusConfig[status] || statusConfig.not_started;
    return (
      <span className={`px-3 py-1 rounded-full text-xs font-semibold border ${config.color}`}>
        {config.label}
      </span>
    );
  };

  const getScoreBadge = (score, maxScore) => {
    if (score === null || score === undefined) return null;
    const percentage = (score / maxScore) * 100;
    let color = 'bg-gray-100 text-gray-700';
    if (percentage >= 90) color = 'bg-green-100 text-green-700';
    else if (percentage >= 75) color = 'bg-blue-100 text-blue-700';
    else if (percentage >= 60) color = 'bg-yellow-100 text-yellow-700';
    else color = 'bg-red-100 text-red-700';
    
    return (
      <span className={`px-3 py-1 rounded-full text-xs font-bold ${color}`}>
        {score}/{maxScore}
      </span>
    );
  };

  const handleExport = () => {
    if (submissions.length === 0) {
      toast.error('No data to export');
      return;
    }

    const exportData = submissions.map(sub => ({
      'Student Name': sub.student?.fullName || 'N/A',
      'Student Email': sub.student?.email || 'N/A',
      'Status': sub.status,
      'Score': sub.score || 'N/A',
      'Max Score': sub.maxScore || 'N/A',
      'Submitted At': sub.submittedAt ? format(new Date(sub.submittedAt), 'MMM dd, yyyy HH:mm') : 'N/A',
      'Time Taken (minutes)': sub.timeTaken || 'N/A'
    }));

    const csv = [
      Object.keys(exportData[0]).join(','),
      ...exportData.map(row => Object.values(row).map(v => `"${v}"`).join(','))
    ].join('\n');

    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `assessment-submissions-${assessment?.assessmentCode || id}-${format(new Date(), 'yyyy-MM-dd')}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success('Exported successfully');
  };

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigateBack(navigate, '/assessments')}
              className="p-2 rounded-lg bg-white border border-gray-200 hover:bg-gray-50 transition-colors"
            >
              <ArrowLeft size={20} />
            </button>
            <div>
              <h1 className="text-2xl font-bold text-[#1a3884] dark:text-[#4f7dff]">
                {assessment?.assessmentName || 'Assessment Submissions'}
              </h1>
              <p className="text-sm text-gray-500 mt-1">
                {submissions.length} student submissions
              </p>
            </div>
          </div>
          <button
            onClick={handleExport}
            className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
          >
            <Download size={18} />
            Export CSV
          </button>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-xl border border-gray-200 p-4 mb-6">
          <div className="flex flex-wrap gap-4 items-center">
            <div className="flex items-center gap-2 text-gray-600">
              <Filter size={16} />
              <span className="text-sm font-medium">Filters</span>
            </div>
            <select
              value={filters.status}
              onChange={(e) => setFilters({ ...filters, status: e.target.value })}
              className="px-3 py-2 rounded-lg border border-gray-200 text-sm focus:ring-2 focus:ring-indigo-400 bg-gray-50"
            >
              <option value="">All Status</option>
              <option value="submitted">Submitted</option>
              <option value="graded">Graded</option>
              <option value="in_progress">In Progress</option>
              <option value="not_started">Not Started</option>
            </select>
            <div className="relative flex-1 max-w-sm">
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                value={filters.search}
                onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                placeholder="Search by student name or email..."
                className="pl-10 pr-4 py-2 w-full rounded-lg border border-gray-200 text-sm focus:ring-2 focus:ring-indigo-400 bg-gray-50"
              />
            </div>
            <button
              onClick={fetchSubmissions}
              className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors text-sm font-medium"
            >
              Apply Filters
            </button>
          </div>
        </div>

        {/* Submissions Table */}
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          {loading ? (
            <div className="p-12 flex items-center justify-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
            </div>
          ) : submissions.length === 0 ? (
            <div className="p-12 text-center">
              <User size={48} className="mx-auto text-gray-300 mb-4" />
              <h3 className="text-lg font-semibold text-gray-900 mb-2">No submissions found</h3>
              <p className="text-gray-500">Students haven't submitted this assessment yet.</p>
            </div>
          ) : (
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Student</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Email</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Score</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Submitted At</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Time Taken</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {submissions.map((submission) => (
                  <tr key={submission._id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="h-8 w-8 rounded-full bg-indigo-100 flex items-center justify-center text-indigo-600 font-bold text-xs">
                          {(submission.student?.fullName || 'S').charAt(0).toUpperCase()}
                        </div>
                        <div className="ml-3">
                          <div className="text-sm font-medium text-gray-900">
                            {submission.student?.fullName || 'Unknown'}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {submission.student?.email || 'N/A'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {getStatusBadge(submission.status)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {getScoreBadge(submission.score, submission.maxScore)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {submission.submittedAt ? format(new Date(submission.submittedAt), 'MMM dd, yyyy HH:mm') : '—'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {submission.timeTaken ? `${Math.round(submission.timeTaken)} min` : '—'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <button className="text-indigo-600 hover:text-indigo-800 flex items-center gap-1">
                        <Eye size={16} />
                        View
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
};

export default AssessmentSubmissions;
